import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { SERVER_URL } from '../config';
import { AuthContext } from '../AuthContext';

export default function VideosPage({ onLogout }) {
  const [videos, setVideos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');

  const { token: ctxToken } = useContext(AuthContext);
  const token = ctxToken || localStorage.getItem('authToken');

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : undefined;

  useEffect(() => {
    async function load() {
      try {
        setError('');
        const res = await axios.get(`${SERVER_URL}/videos/`, authHeaders ? { headers: authHeaders } : undefined);
        setVideos(res.data.videos || []);
      } catch (e) {
        setError('Failed to load videos.');
      }
    }
    load();
  }, [token]);

  const videoUrl = (name) => token
    ? `${SERVER_URL}/videos/${encodeURIComponent(name)}?token=${encodeURIComponent(token)}`
    : `${SERVER_URL}/videos/${encodeURIComponent(name)}`;

  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Video Library</h2>
        {onLogout ? <button onClick={onLogout} style={{ padding: '8px 12px' }}>Logout</button> : null}
      </div>

      {error && <div style={{ color: 'crimson', marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 16 }}>
        <div style={{ border: '1px solid #333', borderRadius: 10, padding: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Videos</div>
          {videos.length === 0 ? (
            <div style={{ opacity: 0.8 }}>No videos uploaded yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {videos.map((v) => (
                <button
                  key={v.name}
                  onClick={() => setSelected(v)}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: selected?.name === v.name ? '2px solid #4c8bf5' : '1px solid #444',
                    background: v.watched ? '#0d1a12' : '#111',
                    color: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ fontWeight: 700 }}>
                    {v.title?.trim() ? v.title : v.name}
                    {v.watched ? <span style={{ fontWeight: 600, opacity: 0.85 }}>  (watched)</span> : null}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>
                    {(v.size / (1024 * 1024)).toFixed(1)} MB • {v.modified?.slice(0, 19).replace('T', ' ')}
                  </div>
                </button>
              ))}
            </div>
          )}

          {token ? (
            <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={async () => {
                  if (!window.confirm('Delete ALL watched videos? This will permanently delete the MP4 files.')) return;
                  try {
                    await axios.delete(`${SERVER_URL}/videos/watched`, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    const res = await axios.get(`${SERVER_URL}/videos/`, { headers: { Authorization: `Bearer ${token}` } });
                    setVideos(res.data.videos || []);
                    if (selected && (res.data.videos || []).every(x => x.name !== selected.name)) setSelected(null);
                  } catch (e) {
                    setError('Failed to delete watched videos.');
                  }
                }}
                style={{ padding: '8px 12px', borderRadius: 10, border: '1px solid #444', background: '#1a0f0f', color: '#fff' }}
              >
                Delete watched
              </button>
            </div>
          ) : null}
        </div>

        <div style={{ border: '1px solid #333', borderRadius: 10, padding: 12 }}>
          {!selected ? (
            <div style={{ opacity: 0.8 }}>Select a video to play.</div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
                <div style={{ fontWeight: 800 }}>{selected.title?.trim() ? selected.title : selected.name}</div>
                <div style={{ fontSize: 12, opacity: 0.8 }}>{selected.watched ? 'watched' : 'unwatched'}</div>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                {token ? (
                  <button
                    onClick={async () => {
                      try {
                        const next = !selected.watched;
                        await axios.post(`${SERVER_URL}/videos/${encodeURIComponent(selected.name)}/watched`, { watched: next }, {
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        // refresh list
                        const res = await axios.get(`${SERVER_URL}/videos/`, { headers: { Authorization: `Bearer ${token}` } });
                        setVideos(res.data.videos || []);
                        const updated = (res.data.videos || []).find(x => x.name === selected.name);
                        if (updated) setSelected(updated);
                      } catch (e) {
                        setError('Failed to update watched state.');
                      }
                    }}
                    style={{ padding: '8px 12px', borderRadius: 10, border: '1px solid #444', background: '#111', color: '#fff' }}
                  >
                    {selected.watched ? 'Mark as unwatched' : 'Mark as watched'}
                  </button>
                ) : null}

                {selected.youtube_url ? (
                  <a href={selected.youtube_url} target="_blank" rel="noreferrer" style={{ color: '#4c8bf5', paddingTop: 8 }}>
                    YouTube original
                  </a>
                ) : null}
              </div>

              <video
                controls
                style={{ width: '100%', borderRadius: 10, background: '#000' }}
                src={videoUrl(selected.name)}
              />
              <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                <a href={videoUrl(selected.name)} target="_blank" rel="noreferrer" style={{ color: '#4c8bf5' }}>
                  Open in new tab
                </a>
                <a href={videoUrl(selected.name)} download style={{ color: '#4c8bf5' }}>
                  Download
                </a>
              </div>
              <div style={{ marginTop: 10, fontSize: 12, opacity: 0.7 }}>
                {token
                  ? 'Note: playback uses a token in the URL (because HTML5 video can’t send auth headers). Don’t share the link.'
                  : 'Public mode: playback URL does not require auth.'}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
